import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { GitFork, CaretDown, Warning, CheckCircle } from "@phosphor-icons/react";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(Number(n || 0));
const today = () => new Date().toISOString().slice(0, 10);
const ddmmyyyy = (iso) => { const [y, m, d] = (iso || "").split("-"); return d && m && y ? `${d}-${m}-${y}` : iso; };
const monYear = (iso) => {
    const m = /^(\d{4})-(\d{2})/.exec(iso || ""); if (!m) return "";
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    return `${months[Number(m[2]) - 1]} ${m[1]}`;
};
const monBefore = (iso) => {
    const d = new Date(iso); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 7);
};

function PartyPicker({ parties, value, onChange, placeholder }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const filt = q.trim() ? parties.filter((p) => (p.name || "").toLowerCase().includes(q.toLowerCase())) : parties;
    const sel = parties.find((p) => p.id === value);
    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
            <PopoverTrigger asChild>
                <button type="button" data-testid="sp-party" className="mt-1 inline-flex h-10 w-full items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-3 text-left text-sm font-medium text-slate-800 shadow-sm hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400">
                    <span className={`truncate ${sel ? "" : "text-slate-400"}`}>{sel?.name || placeholder}</span>
                    <CaretDown size={12} weight="bold" className="shrink-0 text-slate-400" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command shouldFilter={false}>
                    <CommandInput data-testid="sp-party-search" placeholder="Type party name…" value={q} onValueChange={setQ} onKeyDown={(e) => {
                        // v1.29.09 — Read the CURRENT input value at Enter time (state may lag one keystroke) + prefer names that START with the query over substring matches. See Reports.jsx for full rationale.
                        if (e.key !== "Enter") return;
                        const qq = (e.currentTarget.value || "").trim().toLowerCase();
                        if (!qq) return;
                        e.preventDefault();
                        const starts = parties.filter((p) => (p.name || "").toLowerCase().startsWith(qq));
                        const pick = starts.length ? starts : parties.filter((p) => (p.name || "").toLowerCase().includes(qq));
                        if (pick.length) { onChange(pick[0].id); setOpen(false); setQ(""); }
                    }} />
                    <CommandList>
                        <CommandEmpty>No matching party.</CommandEmpty>
                        <CommandGroup>{filt.map((p) => (<CommandItem key={p.id} value={p.name} data-testid={`sp-party-opt-${p.id}`} onSelect={() => { onChange(p.id); setOpen(false); setQ(""); }}>{p.name}</CommandItem>))}</CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}

export default function SplitPartyPeriod() {
    const [parties, setParties] = useState([]);
    const [partyId, setPartyId] = useState("");
    const [splitDate, setSplitDate] = useState(today());
    const [origName, setOrigName] = useState("");
    const [newName, setNewName] = useState("");
    const [carry, setCarry] = useState(false);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/parties").then((r) => setParties((r.data || []).filter((p) => p.active !== false))).catch(() => setParties([]));
    }, []);

    // v1.28.99 — Pre-fill both name fields when the party or split date
    // changes: original gets "<name> (Till <mon-year-before-split>)", new
    // gets "<name> (<mon-year-of-split> Onwards)". Both stay editable.
    const currentParty = useMemo(() => parties.find((p) => p.id === partyId), [parties, partyId]);
    useEffect(() => {
        if (!currentParty) { setOrigName(""); setNewName(""); return; }
        const before = monYear(monBefore(splitDate));
        const after = monYear(splitDate);
        setOrigName(`${currentParty.name} (Till ${before})`);
        setNewName(`${currentParty.name} (${after} Onwards)`);
    }, [currentParty, splitDate]);

    const buildPreview = async () => {
        if (!partyId) return toast.error("Pick a party");
        if (!splitDate) return toast.error("Split date is required");
        if (!origName.trim() || !newName.trim()) return toast.error("Both new names are required");
        if (origName.trim() === newName.trim()) return toast.error("Original and new names must differ");
        setBusy(true);
        try {
            const { data } = await api.post(`/parties/${partyId}/split-preview`, {
                split_date: splitDate, original_new_name: origName.trim(),
                new_party_name: newName.trim(), carry_forward: carry,
            });
            setPreview(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const execute = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/parties/${partyId}/split`, {
                split_date: splitDate, original_new_name: origName.trim(),
                new_party_name: newName.trim(), carry_forward: carry,
            });
            toast.success(`Split done · new party '${data.new_party.name}' created`);
            navigate("/admin/parties");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="mx-auto max-w-3xl space-y-6 p-2" data-testid="split-party-period-page">
            <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-fuchsia-100 text-fuchsia-700"><GitFork size={22} weight="duotone" /></div>
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Party Management</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Split Party Period</h1>
                    <p className="mt-0.5 text-xs text-slate-500">Cut a party's ledger at a date. Rows before the date stay on the renamed original; rows from that date onwards move to a brand-new party in the same portal login.</p>
                </div>
            </div>

            {!preview ? (
                <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Party</Label>
                        <PartyPicker parties={parties} value={partyId} onChange={setPartyId} placeholder="Pick party to split" />
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Split From Date</Label>
                            <Input data-testid="sp-date" type="date" className="mt-1" value={splitDate} onChange={(e) => setSplitDate(e.target.value)} />
                        </div>
                        <div className="flex items-end">
                            <label className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                                <div>
                                    <div className="text-xs font-semibold text-slate-700">Carry Forward Balance</div>
                                    <div className="text-[10px] text-slate-500">Copy the closing balance on the split date as the new party's opening.</div>
                                </div>
                                <Switch data-testid="sp-carry" checked={carry} onCheckedChange={setCarry} />
                            </label>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Original Party New Name</Label>
                        <Input data-testid="sp-orig-name" className="mt-1" value={origName} onChange={(e) => setOrigName(e.target.value)} placeholder="e.g. Prima Traders (Till Aug 2026)" disabled={!partyId} />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">New Party Name</Label>
                        <Input data-testid="sp-new-name" className="mt-1" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Prima Traders (Sep 2026 Onwards)" disabled={!partyId} />
                    </div>
                    <div className="flex justify-end">
                        <Button data-testid="sp-preview" onClick={buildPreview} disabled={busy || !partyId} className="bg-indigo-700 text-white hover:bg-indigo-800">Preview Split</Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-4 rounded-lg border border-fuchsia-300 bg-fuchsia-50/40 p-5 shadow-sm" data-testid="sp-confirm">
                    <div className="flex items-center gap-2 text-fuchsia-900">
                        <Warning size={16} weight="fill" />
                        <div className="font-display text-base font-bold">Confirm the split</div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="rounded-md border border-slate-200 bg-white p-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Original (renamed)</div>
                            <div className="mt-1 font-mono text-sm font-bold text-slate-900" data-testid="sp-preview-orig">{preview.original_new_name}</div>
                            <div className="mt-2 text-[11px] text-slate-500">Keeps {preview.txns_before_count} transactions dated before {ddmmyyyy(splitDate)}.</div>
                        </div>
                        <div className="rounded-md border border-emerald-200 bg-white p-3">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">New party</div>
                            <div className="mt-1 font-mono text-sm font-bold text-slate-900" data-testid="sp-preview-new">{preview.new_party_name}</div>
                            <div className="mt-2 text-[11px] text-slate-500">Takes {preview.txns_from_count} transactions dated on/after {ddmmyyyy(splitDate)}.</div>
                        </div>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white p-3 text-xs">
                        <div><b>Split date:</b> {ddmmyyyy(preview.split_date)}</div>
                        <div className="mt-1"><b>Carry-forward amount:</b>{" "}
                            {carry
                                ? <span className="font-mono text-sm font-bold text-emerald-800" data-testid="sp-preview-carry">{inr(preview.carry_forward_amount)}</span>
                                : <span className="italic text-slate-500" data-testid="sp-preview-carry">Zero — not carried forward (new party opens at ₹0)</span>}
                        </div>
                        <div className="mt-1"><b>Transactions remaining on original:</b> <span data-testid="sp-preview-count">{preview.txns_before_count}</span></div>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                        <Button data-testid="sp-back" variant="outline" onClick={() => setPreview(null)} disabled={busy}>Back to edit</Button>
                        <Button data-testid="sp-execute" onClick={execute} disabled={busy} className="bg-fuchsia-700 text-white hover:bg-fuchsia-800">
                            <CheckCircle size={14} weight="fill" className="mr-1.5" />{busy ? "Splitting…" : "Confirm & Split"}
                        </Button>
                    </div>
                </div>
            )}
            <RecentSplits />
        </div>
    );
}

/**
 * v1.29.00 — Recent Splits card with one-click Undo for anything <24h old.
 * The confirm dialog explicitly warns that the child party will be
 * deleted; anything older than 24h shows the reason instead of the button.
 */
function RecentSplits() {
    const [list, setList] = useState([]);
    const [busy, setBusy] = useState("");
    const load = React.useCallback(async () => {
        try {
            const { data } = await api.get("/activity", { params: { action: "party.split", limit: 20 } });
            const rows = (data?.rows || data?.items || data || []).filter((r) => r.action === "party.split");
            setList(rows);
        } catch { setList([]); }
    }, []);
    useEffect(() => { load(); }, [load]);
    if (!list.length) return null;
    const undo = async (row) => {
        const m = /group=([a-f0-9]{8})/.exec(row.details || "");
        if (!m) return toast.error("Could not parse split group from log entry");
        if (!confirm(`Undo this split? The new child party will be DELETED and every row moved back to the original party. This cannot itself be undone.`)) return;
        setBusy(row.id);
        try {
            // Find full split_group_id by prefix (activity log stored only [:8])
            const full = await api.get("/parties").then((r) => (r.data || []).find((p) => (p.split_group_id || "").startsWith(m[1])));
            if (!full) return toast.error("Split group not found (maybe already undone)");
            const { data } = await api.post(`/parties/split/${full.split_group_id}/undo`);
            toast.success(`Reversed · restored ${data.restored_party.name}`);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Undo failed"); }
        finally { setBusy(""); }
    };
    return (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" data-testid="recent-splits">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Recent splits</div>
            <ul className="divide-y divide-slate-100">
                {list.slice(0, 5).map((row) => {
                    const ageMs = Date.now() - new Date(row.timestamp).getTime();
                    const ageH = ageMs / 3600000;
                    const canUndo = ageH < 24;
                    return (
                        <li key={row.id} className="flex items-center justify-between gap-2 py-2" data-testid={`recent-split-${row.id}`}>
                            <div className="min-w-0 flex-1">
                                <div className="truncate text-xs text-slate-800">{row.details}</div>
                                <div className="text-[10px] text-slate-500">{ddmmyyyy((row.timestamp || "").slice(0, 10))} · {row.user_email} · {ageH < 1 ? `${Math.round(ageH * 60)}m ago` : `${ageH.toFixed(1)}h ago`}</div>
                            </div>
                            {canUndo ? (
                                <Button size="sm" variant="outline" data-testid={`undo-${row.id}`} onClick={() => undo(row)} disabled={busy === row.id} className="border-rose-300 text-rose-800 hover:bg-rose-50">
                                    {busy === row.id ? "Undoing…" : "Undo"}
                                </Button>
                            ) : (
                                <span className="text-[10px] italic text-slate-400">Undo window closed (24 h)</span>
                            )}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
